<template>
  <div class="flex flex-col mt-5 w-full border h-full rounded-lg">
    <!-- Opener Button -->
    <button class="flex flex-row px-4 py-2.5 justify-between transition-colors items-center rounded-lg"
    :class="{ 'hover:bg-neutral-10': !expanded }" @click="handleExpand()">
      <div class="flex flex-row items-center gap-3">
        <div class="flex gap-1 items-center">
          <div v-if="!locked" class="w-1.5 h-1.5 rounded-full mx-2" :style="{ backgroundColor: creativeTestDay.liveCount > 0 ? '#00A879' : '#A4ACB9' }" />
          <button class="group rounded-md p-1.5 items-center justify-center transition-colors "
          :class="locked ? 'text-icon-disabled pointer-events-none' : 'hover:bg-neutral-50 text-icon-normal hover:text-icon-muted'"
          @click.stop="$emit('handlePin')">
            <UnpinIcon v-if="isPinned" class="flex-shrink-0" />
            <PinIcon v-else class="flex-shrink-0" />
          </button>
          <BaseText type="label" size="sm" class="text-text-muted">
            {{ formatDate(creativeTestDay.date) }}
          </BaseText>
        </div>
        <div v-if="creativeTestDay.liveCount > 0" class="py-0.5 px-2.5 bg-primary-green-10 rounded-xl">
          <BaseText type="label" size="xs" class="text-primary-green-200">
            {{ creativeTestDay.liveCount }}/{{ creativeTestDay.count }} Ads
            Running
          </BaseText>
        </div>
        <div v-if="creativeTestDay.liveCount === 1" class="flex flex-row gap-1 py-0.5 px-2 bg-secondary-yellow-10 rounded-xl">
          <img src="../../../assets/icons/winner.svg">
          <BaseText type="label" size="xs" class="text-secondary-yellow-200">
            Winner Identified
          </BaseText>
        </div>
      </div>
      <div class="transform transition-transform" :style="{ transform: expanded ? 'scaleY(-1)' : 'scaleY(1)' }">
        <ChevronIcon />
      </div>
    </button>
    <transition name="ctborder">
      <div v-if="expanded" class="h-0 w-full border-t border-border-normal" />
    </transition>
    <!-- Dropdown -->
    <div class="creative-test-dropdown relative" :class="locked ? 'pointer-events-none select-none overflow-y-hidden' : 'overflow-y-scroll'"
    :style="{ height: expanded ? `${(creativeTestDay.count > 10 ? 10 : creativeTestDay.count) * 52 + 16}px` : '0px' }"
    @transitionend="handleExpansionEnd()">
      <CreativeTestsLockedOverlay v-if="locked" class="absolute top-0 left-0 right-0 z-50 rounded-b-lg" style="height: 224px"
      :text="`Creative tests are locked, start tracking ${brandName ?? 'this brand'} to unlock.`" />
      <div class="p-2">
        <!-- Loading -->
        <div v-if="computedAds.length === 0" class="absolute left-1/2 transform -translate-x-1/2 -translate-y-1/2" style="top: 45%">
          <BaseLoadingLogo />
        </div>
        <!-- Ads -->
        <transition-group>
          <div v-for="(ad, index) in computedAds" :key="`${creativeTestDay.date}-${ad.ad_id}-${index}`"
          class="group flex flex-row w-full items-center p-2 rounded-lg transition-colors hover:bg-neutral-25 cursor-pointer"
          @click="$emit('openDetailsDrawer', ad)">
            <div class="flex flex-row gap-3 items-center flex-shrink-0">
              <!-- Thumbnail -->
              <ThumbnailPreview :src="ad.thumbnail || (ad.cards?.length && ad?.cards[0]?.thumbnail) || (ad.cards?.length && ad?.cards[0]?.image) || ad.image || ''"
              :ref="`ad-thumbnail-${ad.id}`"  @mouseenter.native="hoveredAdPreview = ad" @mouseleave.native="hoveredAdPreview = null"
              @click.stop.prevent="attemptManualPreviewPlay = !attemptManualPreviewPlay" :disable-loading="locked" />
              <!-- Format Icon -->
              <VideoFormatIcon v-if="ad.type === 'video'" :width="16" :height="16" stroke="#5E6678" />
              <ImageFormatIcon v-else-if="ad.type === 'image'" :width="16" :height="16" stroke="#5E6678" />
              <CarouselFormatIcon v-else :width="16" :height="16" stroke="#5E6678" />
            </div>
            <!-- Runtime Bar -->
            <div class="runtime-bar relative p-2 ml-3 rounded-lg overflow-hidden" :class="ad.live ? 'live bg-primary-green-10' : 'bg-neutral-25'"
            :style="{ width: `${calculateWidthForAd(ad)}%`}">
              <BaseText type="label" size="xs" class="relative ml-1 z-20 w-full truncate" 
              :class="ad.live ? 'text-primary-green-300' : 'text-text-muted'">
                {{ formatDisplayDuration(ad) }}
              </BaseText>
              <div class="absolute top-0 bottom-0 left-0 right-0 opacity-0 transition-opacity group-hover:opacity-20 z-10"
              :class="ad.live ? 'bg-primary-green-50' : 'bg-neutral-200'" />
            </div>
          </div>
        </transition-group>
        <infinite-loading
          v-if="advertisements.length && !locked"
          :identifier="infiniteId"
          @infinite="($state) => fetchAds($state)"
        >
          <div slot="spinner">
            <BaseLoadingLogo :margin="2" />
          </div>
          <div slot="no-more" />
          <div slot="no-results" />
        </infinite-loading>
      </div>
    </div>

    <transition>
      <BasePopupVideoPreview
        v-if="hoveredAdPreview && adPreviewVideoSrc && !locked"
        class="fixed z-50 transform -translate-y-1/2"
        :style="{ top: `${adPreviewPosition.y}px`, left: `${adPreviewPosition.x}px` }"
        :video-src="adPreviewVideoSrc"
        :attempt-manual-play="attemptManualPreviewPlay"
      />
      <BasePopupThumbnailPreview
        v-else-if="hoveredAdPreview && !locked"
        class="fixed z-50 transform -translate-y-1/2"
        :style="{ top: `${adPreviewPosition.y}px`, left: `${adPreviewPosition.x}px` }"
        :thumbnail-src="adPreviewImageSrc"
      />
    </transition>
  </div>
</template>

<script>
import moment from 'moment'
import ForeplayAPI from '@/api/foreplayServer'
import InfiniteLoading from 'vue-infinite-loading'
import { getCreativeTestDayAdsMockData } from '@/utils/spyder/mockDataUtils'

import ThumbnailPreview from './ThumbnailPreview.vue'
import CreativeTestsLockedOverlay from './CreativeTestsLockedOverlay.vue'

// Icons
import ImageFormatIcon from '../../globals/Icons/FilterIcons/ImageFormatIcon.vue'
import VideoFormatIcon from '../../globals/Icons/FilterIcons/VideoFormatIcon.vue'
import CarouselFormatIcon from '../../globals/Icons/FilterIcons/CarouselFormatIcon.vue'
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import UnpinIcon from '../../globals/Icons/SpyderV2Icons/UnpinIcon.vue'
import PinIcon from '../../globals/Icons/SpyderV2Icons/PinIcon.vue'

const EXPAND_TRANSITION_DURATION = 150

export default {
  name: 'CreativeTestDay',
  components: {
    InfiniteLoading,
    ThumbnailPreview,
    CreativeTestsLockedOverlay,
    ImageFormatIcon,
    VideoFormatIcon,
    CarouselFormatIcon,
    ChevronIcon,
    UnpinIcon,
    PinIcon
  },
  props: {
    creativeTestDay: {
      type: Object,
      required: true,
    },
    isPinned: {
      type: Boolean,
      default: false
    },
    brandName: {
      type: String,
      required: true
    },
    locked: {
      type: Boolean,
      default: false
    },
    initExpanded: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      advertisements: [],
      nextId: null,
      infiniteId: +new Date(),

      // UI
      expanded: false,
      hoveredAdPreview: null,
      adPreviewPosition: { x: 0, y: 0 },
      attemptManualPreviewPlay: false,
      EXPAND_TRANSITION_DURATION
    }
  },
  computed: {
    computedAds () {
      return this.advertisements.map((ad) => {
        return {
          ...ad,
          runningDuration: this.parseRunningDuration(ad.startedRunning, this.getAdEndTime(ad)),
        }
      })
    },
    longestDuration () {
      if (!this.computedAds?.length) return 0
      return Math.max(
        ...this.computedAds.map((ad) =>
          ad.runningDuration
        )
      )
    },
    adPreviewImageSrc () {
      return (
        this.hoveredAdPreview?.image 
        ?? this.hoveredAdPreview?.thumbnail 
        ?? this.hoveredAdPreview?.cards?.[0]?.image 
        ?? this.hoveredAdPreview?.cards?.[0]?.thumbnail
        ?? ''
      )
    },
    adPreviewVideoSrc () {
      return (
        this.hoveredAdPreview?.video
        ?? this.hoveredAdPreview?.cards?.[0]?.video
        ?? null
      )
    }
  },
  watch: {
    hoveredAdPreview (ad) {
      if (!ad) return
      const thumbnailElement = this.$refs[`ad-thumbnail-${ad.id}`][0].$el
      const thumbnailRect = thumbnailElement.getBoundingClientRect()
      this.adPreviewPosition = {
        x: thumbnailRect.left + thumbnailRect.width + 6,
        y: thumbnailRect.top + window.scrollY + thumbnailRect.height / 2
      }
    }
  },
  created () {
    if (this.initExpanded) this.handleExpand()
  },
  methods: {
    handleExpand () {
      if (!this.expanded) {
        this.expanded = true;
        this.fetchAds();
      } else {
        this.expanded = false;
      }
    },
    handleExpansionEnd () {
      // Unload state/dom when collapsing
      if (!this.expanded) {
        this.advertisements = []
        this.nextId = null
        this.infiniteId = +new Date()
      }
    },
    async fetchAds ($state = null) {
      const { dateStart, dateEnd } = this.createStartAndEndOfDateTimestamps(this.creativeTestDay.date)

      if (this.locked) {
        this.advertisements = getCreativeTestDayAdsMockData()
        return
      }
      if ($state && !this.nextId) {
        $state.complete()
        return
      }

      try {
        const { data: ads, nextPage } = await ForeplayAPI.Ads.getSpyderAds(
          this.$route.params.id,
          this.nextId, 
          {
            sort: 'longest',
            startedRunningStart: dateStart,
            startedRunningEnd: dateEnd
          }
        )
        this.nextId = nextPage

        this.advertisements = [...this.advertisements, ...ads]
      } catch (error) {
        console.error('Error fetching ads by date', error)
      } finally {
        this.infiniteId += 1
        if ($state) {
          $state.loaded()
        }
      }
    },

    // DATE PARSING
    // The ads endpoint filters on epoch millis, so widen the 'YYYY-MM-DD' day into a UTC range
    createStartAndEndOfDateTimestamps (date) {
      const dateStart = moment.utc(date, 'YYYY-MM-DD').valueOf()
      const dateEnd = dateStart + 86399999
      return { dateStart, dateEnd }
    },
    parseRunningDuration (start, end) {
      let endTime
      if (end && !isNaN(end._seconds)) {
        endTime = end._seconds * 1000
      } else {
        endTime = new Date().getTime()
      }
      const startDate = new moment.utc(start)
      const endDate = new moment.utc(endTime)

      return Math.ceil(moment.duration(endDate.diff(startDate)).as('days'))
    },
    getAdEndTime (ad) {
      return ad.live ? new Date().getTime() : ad?.lastChecked || ad?.last_checked
    },

    // DATE FORMATTING
    formatDate (dateString) {
      const options = { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC'}
      const date = new Date(`${dateString}T00:00:00Z`)
      return date.toLocaleDateString(undefined, options)
    },
    formatDisplayDuration (ad) {
      const runningDuration = ad.runningDuration
      const displayDays = runningDuration === 0 ? '<1' : runningDuration
      const daysText = runningDuration > 1 ? 'Days' : 'Day'
      return `${displayDays} ${daysText}`
    },

    // UI
    calculateWidthForAd (ad) {
      const calculatedWidth = (ad.runningDuration / this.longestDuration) * 100
      const minWidth = 4

      return Math.max(calculatedWidth, minWidth)
    },
  }
}
</script>

<style scoped>
.creative-test-dropdown {
  transition: height 150ms ease-in-out;
}
.creative-test-dropdown::-webkit-scrollbar {
  width: 3px;
}
.creative-test-dropdown::-webkit-scrollbar-thumb {
  background-color: #DFE1E7;
  border-radius: 18px;
}

.runtime-bar {
  background-image: repeating-linear-gradient(-60deg, transparent, rgba(223, 225, 231, 0.2) 8px, rgba(223, 225, 231, 0.2) 8px, transparent 20px);
}
.runtime-bar.live {
  background-image: repeating-linear-gradient(-60deg, transparent, rgba(101, 210, 166, 0.1) 8px, rgba(101, 210, 166, 0.1) 8px, transparent 20px);
}

/* ======== VUE TRANSITIONS ======== */
.v-enter-active, .v-leave-active {
  transition: opacity 200ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}
.ctborder-leave-active {
  transition: opacity 200ms ease-in-out;
}
.ctborder-enter-from, .ctborder-enter {
  opacity: 1;
}
.ctborder-leave-to {
  opacity: 0;
}
.ctborder-enter-to, .ctborder-leave-from {
  opacity: 1;
}
</style>